import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import type { Task } from '../../../tasks/types/task.types';
import { toast } from '@/components/ui/use-toast';
import GanttDragConfirmDialog from './gantt/GanttDragConfirmDialog';
import { useGanttDependencies } from './gantt/useGanttDependencies';
import { useGanttDrag } from './gantt/useGanttDrag';

interface GanttViewProps {
  tasks: Task[];
  matterId: string;
  selectedTaskId?: string | null;
  onSelectTask?: (taskId: string | null) => void;
  onTasksUpdated?: () => void;
}

type ZoomLevel = 'day' | 'week' | 'month';

const STATUS_COLORS: Record<string, string> = {
  todo: 'bg-gray-400',
  in_progress: 'bg-blue-500',
  review: 'bg-yellow-500',
  done: 'bg-green-500',
  archived: 'bg-gray-300',
};

const STATUS_LABELS: Record<string, string> = {
  todo: 'To Do',
  in_progress: 'In Progress',
  review: 'In Review',
  done: 'Done',
  archived: 'Archived',
};

interface PhaseGroup {
  phaseName: string;
  phaseOrder: number;
  tasks: Task[];
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function diffDays(a: Date, b: Date): number {
  return Math.round((a.getTime() - b.getTime()) / (1000 * 60 * 60 * 24));
}

function formatDateShort(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function isWeekend(date: Date): boolean {
  const day = date.getDay();
  return day === 0 || day === 6;
}

const GanttView: React.FC<GanttViewProps> = ({ tasks, matterId, selectedTaskId, onSelectTask, onTasksUpdated }) => {
  const [zoom, setZoom] = useState<ZoomLevel>('week');
  const [hoveredTaskId, setHoveredTaskId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Dependency graph for drag cascade + dependency arrows
  const { graph: dependencyGraph, refresh: refreshDeps } = useGanttDependencies(matterId);

  // Compute date range from tasks
  const { startDate, endDate, totalDays } = useMemo(() => {
    const now = new Date();
    let earliest = now;
    let latest = addDays(now, 30);

    for (const t of tasks) {
      if (t.start_date) {
        const d = new Date(t.start_date);
        if (d < earliest) earliest = d;
      }
      if (t.due_date) {
        const d = new Date(t.due_date);
        if (d < earliest) earliest = d;
        if (d > latest) latest = d;
      }
      if (t.created_at) {
        const d = new Date(t.created_at);
        if (d < earliest) earliest = d;
      }
    }

    const start = addDays(earliest, -7);
    const end = addDays(latest, 14);
    return {
      startDate: start,
      endDate: end,
      totalDays: Math.max(diffDays(end, start), 1),
    };
  }, [tasks]);

  const dayWidth = zoom === 'day' ? 40 : zoom === 'week' ? 12 : 4;
  const chartWidth = totalDays * dayWidth;
  const labelColWidth = 250;
  const totalWidth = labelColWidth + chartWidth;
  const todayOffset = diffDays(new Date(), startDate) * dayWidth;

  const scrollToToday = useCallback(() => {
    if (!scrollRef.current) return;
    const containerWidth = scrollRef.current.clientWidth;
    scrollRef.current.scrollLeft = Math.max(0, (labelColWidth + todayOffset) - containerWidth / 2);
  }, [todayOffset, labelColWidth]);

  useEffect(() => {
    const timer = setTimeout(scrollToToday, 50);
    return () => clearTimeout(timer);
  }, [scrollToToday]);

  // Group tasks by phase
  const phaseGroups = useMemo((): PhaseGroup[] => {
    const groups = new Map<string, PhaseGroup>();
    const ungrouped: Task[] = [];

    for (const task of tasks) {
      const phaseName = task.court_metadata?.phase_name;
      if (phaseName) {
        if (!groups.has(phaseName)) {
          groups.set(phaseName, {
            phaseName,
            phaseOrder: task.court_metadata?.phase_order ?? 999,
            tasks: [],
          });
        }
        groups.get(phaseName)!.tasks.push(task);
      } else {
        ungrouped.push(task);
      }
    }

    const sorted = Array.from(groups.values()).sort((a, b) => a.phaseOrder - b.phaseOrder);
    if (ungrouped.length > 0) {
      sorted.push({ phaseName: 'Other Tasks', phaseOrder: 9999, tasks: ungrouped });
    }
    return sorted;
  }, [tasks]);

  // Column headers
  const columnHeaders = useMemo(() => {
    const headers: { label: string; left: number; width: number }[] = [];
    const d = new Date(startDate);

    if (zoom === 'day') {
      while (d <= endDate) {
        const left = diffDays(d, startDate) * dayWidth;
        headers.push({ label: formatDateShort(d), left, width: dayWidth });
        d.setDate(d.getDate() + 1);
      }
    } else if (zoom === 'week') {
      while (d.getDay() !== 1) d.setDate(d.getDate() + 1);
      while (d <= endDate) {
        const left = diffDays(d, startDate) * dayWidth;
        headers.push({ label: formatDateShort(d), left, width: 7 * dayWidth });
        d.setDate(d.getDate() + 7);
      }
    } else {
      d.setDate(1);
      while (d <= endDate) {
        const left = diffDays(d, startDate) * dayWidth;
        const monthLabel = d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
        const daysInMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
        headers.push({ label: monthLabel, left, width: daysInMonth * dayWidth });
        d.setMonth(d.getMonth() + 1);
      }
    }
    return headers;
  }, [startDate, endDate, dayWidth, zoom]);

  // Weekend stripes
  const weekendStripes = useMemo(() => {
    if (zoom === 'month') return [];
    const stripes: { left: number; width: number }[] = [];
    const d = new Date(startDate);
    while (d <= endDate) {
      if (isWeekend(d)) {
        const left = diffDays(d, startDate) * dayWidth;
        stripes.push({ left, width: dayWidth });
      }
      d.setDate(d.getDate() + 1);
    }
    return stripes;
  }, [startDate, endDate, dayWidth, zoom]);

  // Task bar position calculator
  const getTaskBar = useCallback((task: Task) => {
    const taskStart = task.start_date
      ? new Date(task.start_date)
      : task.created_at
        ? new Date(task.created_at)
        : new Date();
    const taskEnd = task.due_date ? new Date(task.due_date) : addDays(taskStart, 7);

    const left = Math.max(diffDays(taskStart, startDate) * dayWidth, 0);
    const width = Math.max(diffDays(taskEnd, taskStart) * dayWidth, dayWidth);
    const isMandated = !!(task.court_metadata?.is_court_mandated || task.source === 'court_rule');
    const hasDeadline = !!task.court_metadata?.deadline_calculation_trace;

    return { left, width, isMandated, hasDeadline, taskEnd };
  }, [startDate, dayWidth]);

  const ROW_HEIGHT = 32;
  const PHASE_HEADER_HEIGHT = 28;

  // ── Drag-to-reschedule ──
  const {
    dragState,
    handleMouseDown: onDragStart,
    showConfirmDialog,
    pendingChanges,
    draggedTaskChange,
    saving,
    confirmDrag,
    rejectDrag,
    clearDragState,
  } = useGanttDrag({
    matterId,
    tasks,
    dependencyGraph,
    dayWidth,
    startDate,
    scrollRef,
    labelColWidth,
    getTaskBar,
  });

  // Handle drag confirmation → save
  const handleDragConfirm = useCallback(async () => {
    const result = await confirmDrag();

    if (result.result === 'success') {
      const updatedCount = result.updated ?? (draggedTaskChange ? pendingChanges.length + 1 : 0);
      toast({
        title: 'Rescheduled',
        description: `${updatedCount} task${updatedCount !== 1 ? 's' : ''} updated successfully.`,
      });
      clearDragState();
      onTasksUpdated?.();
      refreshDeps();
      return;
    }

    if (result.result === 'mandated_conflict') {
      const msg = result.errorDetail?.message
        || (result.errorDetail?.task_title
          ? `Cannot reschedule ${result.errorDetail.task_title}.`
          : 'Cannot reschedule a court-mandated task.');
      toast({ title: 'Reschedule blocked', description: msg, variant: 'destructive' });
      rejectDrag();
      return;
    }

    if (result.result === 'cascade_violation') {
      const blockingTitle = result.errorDetail?.blocking_task_title || 'a mandated task';
      const blockingDate = result.errorDetail?.blocking_task_due_date || 'its statutory deadline';
      const msg = result.errorDetail?.message
        || `Cannot reschedule ${draggedTaskChange?.title || 'this task'} — it would push ${blockingTitle} past its statutory deadline of ${blockingDate}.`;
      toast({ title: 'Deadline conflict', description: msg, variant: 'destructive' });
      rejectDrag();
      return;
    }

    toast({
      title: 'Error',
      description: result.errorDetail?.message || 'Failed to reschedule tasks.',
      variant: 'destructive',
    });
  }, [confirmDrag, draggedTaskChange, pendingChanges.length, clearDragState, onTasksUpdated, refreshDeps, rejectDrag]);

  // Task Y positions for dependency arrows
  const taskYMap = useMemo(() => {
    const map = new Map<string, number>();
    let y = 0;
    for (const group of phaseGroups) {
      y += PHASE_HEADER_HEIGHT;
      for (const task of group.tasks) {
        map.set(task.id, y + ROW_HEIGHT / 2);
        y += ROW_HEIGHT;
      }
    }
    return map;
  }, [phaseGroups]);

  // Phase-flow arrows (between phases)
  const phaseFlowArrows = useMemo(() => {
    if (phaseGroups.length < 2) return [];
    const arrows: Array<{ fromX: number; fromY: number; toX: number; toY: number }> = [];

    for (let i = 0; i < phaseGroups.length - 1; i++) {
      const currentPhase = phaseGroups[i];
      const nextPhase = phaseGroups[i + 1];
      if (currentPhase.tasks.length === 0 || nextPhase.tasks.length === 0) continue;

      const lastTask = currentPhase.tasks[currentPhase.tasks.length - 1];
      const firstTask = nextPhase.tasks[0];
      const lastBar = getTaskBar(lastTask);
      const firstBar = getTaskBar(firstTask);
      const fromY = taskYMap.get(lastTask.id) ?? 0;
      const toY = taskYMap.get(firstTask.id) ?? 0;

      arrows.push({ fromX: lastBar.left + lastBar.width, fromY, toX: firstBar.left, toY });
    }
    return arrows;
  }, [phaseGroups, getTaskBar, taskYMap]);

  // Task-level dependency arrows (from dependency graph)
  const taskDepArrows = useMemo(() => {
    const arrows: Array<{ fromX: number; fromY: number; toX: number; toY: number; isAffected: boolean }> = [];
    const taskMap = new Map(tasks.map(t => [t.id, t]));

    for (const [taskId, downstreamIds] of dependencyGraph.downstream) {
      const fromTask = taskMap.get(taskId);
      if (!fromTask) continue;
      const fromBar = getTaskBar(fromTask);
      const fromY = taskYMap.get(taskId) ?? 0;

      for (const depId of downstreamIds) {
        const toTask = taskMap.get(depId);
        if (!toTask) continue;
        const toBar = getTaskBar(toTask);
        const toY = taskYMap.get(depId) ?? 0;
        if (fromY === 0 && toY === 0) continue;

        const isAffected = !!(dragState && dragState.affectedTaskIds.includes(depId));
        arrows.push({ fromX: fromBar.left + fromBar.width, fromY, toX: toBar.left, toY, isAffected });
      }
    }
    return arrows;
  }, [tasks, dependencyGraph, getTaskBar, taskYMap, dragState]);

  // Chart body height
  const chartBodyHeight = useMemo(() => {
    let h = 0;
    for (const group of phaseGroups) {
      h += PHASE_HEADER_HEIGHT;
      h += group.tasks.length * ROW_HEIGHT;
    }
    return h;
  }, [phaseGroups]);

  // Click handler for task bars / labels
  const handleTaskClick = useCallback((taskId: string) => {
    if (dragState) return; // Don't open panel during drag
    onSelectTask?.(selectedTaskId === taskId ? null : taskId);
  }, [dragState, onSelectTask, selectedTaskId]);

  if (tasks.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500 dark:text-gray-400">
        No tasks to display in Gantt view.
      </div>
    );
  }

  return (
    <div className="space-y-3">
        {/* Zoom Controls + Legend */}
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Zoom:</span>
          {(['day', 'week', 'month'] as ZoomLevel[]).map((level) => (
            <button
              key={level}
              onClick={() => setZoom(level)}
              className={`px-2.5 py-1 text-xs rounded font-medium transition-colors ${
                zoom === level
                  ? 'bg-lex-accent text-lex-accent-contrast'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {level.charAt(0).toUpperCase() + level.slice(1)}
            </button>
          ))}

          <div className="ml-auto flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
            {Object.entries(STATUS_COLORS).filter(([k]) => k !== 'archived').map(([status, color]) => (
              <div key={status} className="flex items-center gap-1">
                <div className={`w-3 h-3 rounded-sm ${color}`} />
                <span>{STATUS_LABELS[status]}</span>
              </div>
            ))}
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-sm border-l-2 border-red-500 bg-lex-accent" />
              <span>Mandated</span>
            </div>
            <div className="flex items-center gap-1">
              <svg className="w-3 h-3 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              <span>Locked</span>
            </div>
          </div>
        </div>

        {/* Gantt Chart — wrapper reserves space; chart is absolute inside */}
        <div
          className="relative border border-gray-200 dark:border-gray-700 rounded-lg"
          style={{ height: 32 + chartBodyHeight }}
        >
          <div
            className="absolute inset-0 overflow-x-auto rounded-lg"
            ref={scrollRef}
          >
            <div style={{ width: totalWidth }}>
              {/* Column header row */}
              <div className="flex border-b border-gray-200 dark:border-gray-700" style={{ height: 32 }}>
                <div
                  className="sticky left-0 z-20 flex-shrink-0 flex items-center px-3 bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700"
                  style={{ width: labelColWidth }}
                >
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Task</span>
                </div>
                <div className="relative bg-gray-50 dark:bg-gray-900" style={{ width: chartWidth }}>
                  {columnHeaders.map((col, i) => (
                    <div
                      key={i}
                      className="absolute top-0 h-full flex items-center justify-center text-xs text-gray-500 dark:text-gray-400 border-r border-gray-100 dark:border-gray-800"
                      style={{ left: col.left, width: col.width }}
                    >
                      {col.label}
                    </div>
                  ))}
                </div>
              </div>

              {/* Chart body */}
              <div className="relative">
                {/* Weekend shading */}
                {weekendStripes.length > 0 && (
                  <div
                    className="absolute top-0 bottom-0 pointer-events-none z-0"
                    style={{ left: labelColWidth, width: chartWidth }}
                  >
                    {weekendStripes.map((stripe, i) => (
                      <div
                        key={i}
                        className="absolute top-0 bottom-0 bg-gray-100/50 dark:bg-gray-800/30"
                        style={{ left: stripe.left, width: stripe.width }}
                      />
                    ))}
                  </div>
                )}

                {/* Today marker */}
                {todayOffset > 0 && todayOffset < chartWidth && (
                  <div
                    className="absolute top-0 bottom-0 z-10 pointer-events-none"
                    style={{ left: labelColWidth + todayOffset }}
                  >
                    <div className="absolute top-0 bottom-0 w-px bg-red-400" />
                    <div className="absolute -top-0.5 -translate-x-1/2 left-px">
                      <span className="text-[9px] font-semibold text-red-500 bg-white dark:bg-gray-900 px-1 rounded shadow-sm border border-red-200 dark:border-red-800 whitespace-nowrap">
                        Today
                      </span>
                    </div>
                  </div>
                )}

                {/* Dependency arrows SVG overlay */}
                {(phaseFlowArrows.length > 0 || taskDepArrows.length > 0) && (
                  <svg
                    className="absolute top-0 pointer-events-none z-[5]"
                    style={{ left: labelColWidth }}
                    width={chartWidth}
                    height={chartBodyHeight}
                  >
                    <defs>
                      <marker id="gantt-arrowhead" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
                        <polygon points="0 0, 6 2, 0 4" className="fill-lex-accent" />
                      </marker>
                      <marker id="gantt-arrowhead-amber" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
                        <polygon points="0 0, 6 2, 0 4" className="fill-amber-500" />
                      </marker>
                      <marker id="gantt-arrowhead-task" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
                        <polygon points="0 0, 6 2, 0 4" className="fill-lex-accent" />
                      </marker>
                    </defs>

                    {/* Phase-flow arrows (dashed) */}
                    {phaseFlowArrows.map((arrow, i) => {
                      const gap = 8;
                      const fx = arrow.fromX + gap;
                      const tx = Math.max(arrow.toX - gap, fx + 10);
                      const midX = (fx + tx) / 2;
                      return (
                        <path
                          key={`phase-${i}`}
                          d={`M ${fx} ${arrow.fromY} C ${midX} ${arrow.fromY}, ${midX} ${arrow.toY}, ${tx} ${arrow.toY}`}
                          fill="none"
                          className="stroke-lex-accent"
                          strokeWidth="1.5"
                          strokeDasharray="4 3"
                          markerEnd="url(#gantt-arrowhead)"
                          opacity={0.7}
                        />
                      );
                    })}

                    {/* Task-level dependency arrows (solid) */}
                    {taskDepArrows.map((arrow, i) => {
                      const gap = 4;
                      const fx = arrow.fromX + gap;
                      const tx = Math.max(arrow.toX - gap, fx + 10);
                      const midX = (fx + tx) / 2;
                      return (
                        <path
                          key={`dep-${i}`}
                          d={`M ${fx} ${arrow.fromY} C ${midX} ${arrow.fromY}, ${midX} ${arrow.toY}, ${tx} ${arrow.toY}`}
                          fill="none"
                          className={arrow.isAffected ? 'stroke-amber-500' : 'stroke-lex-accent'}
                          strokeWidth={arrow.isAffected ? 2 : 1}
                          markerEnd={arrow.isAffected ? 'url(#gantt-arrowhead-amber)' : 'url(#gantt-arrowhead-task)'}
                          opacity={arrow.isAffected ? 1 : 0.5}
                        />
                      );
                    })}
                  </svg>
                )}

                {/* Phase groups + task rows */}
                {phaseGroups.map((group) => (
                  <React.Fragment key={group.phaseName}>
                    {/* Phase header row */}
                    <div className="flex" style={{ height: PHASE_HEADER_HEIGHT }}>
                      <div
                        className="sticky left-0 z-20 flex-shrink-0 flex items-center px-3 bg-lex-accent-soft border-b border-gray-100 dark:border-gray-800 border-r border-gray-200 dark:border-gray-700"
                        style={{ width: labelColWidth }}
                      >
                        <span className="text-xs font-semibold text-lex-accent truncate">
                          {group.phaseName}
                        </span>
                      </div>
                      <div
                        className="bg-lex-state-hover border-b border-gray-100 dark:border-gray-800"
                        style={{ width: chartWidth }}
                      />
                    </div>

                    {/* Task rows */}
                    {group.tasks.map((task) => {
                      const bar = getTaskBar(task);
                      const isMandated = bar.isMandated;
                      const isDragging = dragState?.taskId === task.id;
                      const isSelected = selectedTaskId === task.id;
                      const isAffected = dragState?.affectedTaskIds.includes(task.id);
                      const barLeft = isDragging ? dragState.currentLeft : bar.left;

                      return (
                        <div
                          key={task.id}
                          className="flex"
                          style={{ height: ROW_HEIGHT }}
                          onMouseEnter={() => { if (!dragState) setHoveredTaskId(task.id); }}
                          onMouseLeave={() => setHoveredTaskId(null)}
                        >
                          {/* Sticky task label */}
                          <div
                            className={`sticky left-0 z-20 flex-shrink-0 flex items-center px-3 border-b border-gray-100 dark:border-gray-800 border-r border-gray-200 dark:border-gray-700 cursor-pointer transition-colors ${
                              isSelected
                                ? 'bg-lex-accent-soft'
                                : 'bg-white dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800'
                            }`}
                            style={{ width: labelColWidth }}
                            onClick={() => handleTaskClick(task.id)}
                          >
                            {/* Lock icon for mandated tasks */}
                            {isMandated && (
                              <svg className="w-3 h-3 mr-1.5 flex-shrink-0 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                              </svg>
                            )}
                            <span className="text-xs text-gray-700 dark:text-gray-300 truncate" title={task.title}>
                              {task.title}
                            </span>
                          </div>

                          {/* Chart bar area */}
                          <div
                            className="relative border-b border-gray-100 dark:border-gray-800"
                            style={{ width: chartWidth }}
                          >
                            {/* Ghost outline at original position during drag */}
                            {isDragging && dragState.daysDelta !== 0 && (
                              <div
                                className="absolute top-1 rounded-sm border-2 border-dashed border-gray-400 dark:border-gray-500 opacity-40"
                                style={{
                                  left: dragState.originalLeft,
                                  width: Math.max(bar.width, 20),
                                  height: ROW_HEIGHT - 8,
                                }}
                              />
                            )}

                            {/* Task bar */}
                            <div
                              className={`absolute top-1 rounded-sm transition-shadow ${
                                STATUS_COLORS[task.status] || 'bg-gray-400'
                              } ${isMandated ? 'border-l-[3px] border-red-500' : ''} ${
                                isSelected ? 'ring-2 ring-lex-accent ring-offset-1' : ''
                              } ${isAffected ? 'ring-2 ring-amber-400' : ''} ${
                                isDragging ? 'shadow-lg opacity-90 z-10' : ''
                              }`}
                              style={{
                                left: barLeft,
                                width: Math.max(bar.width, 20),
                                height: ROW_HEIGHT - 8,
                                cursor: isMandated ? 'not-allowed' : dragState ? 'grabbing' : 'grab',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!dragState) handleTaskClick(task.id);
                              }}
                              onMouseDown={(e) => onDragStart(task.id, e)}
                            >
                              {/* Lock icon inside mandated bar */}
                              {isMandated && (
                                <div className="absolute inset-0 flex items-center justify-end pr-1 pointer-events-none">
                                  <svg className="w-3 h-3 text-white/70" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              )}
                            </div>

                            {/* Drag date tooltip */}
                            {isDragging && dragState.daysDelta !== 0 && (
                              <div
                                className="absolute z-40 bg-gray-900 text-white text-[10px] font-medium px-2 py-1 rounded shadow-lg pointer-events-none whitespace-nowrap"
                                style={{ left: barLeft, top: -22 }}
                              >
                                {formatDateShort(dragState.newStartDate)} &rarr; {formatDateShort(dragState.newDueDate)}
                                <span className="ml-1 text-gray-300">
                                  ({dragState.daysDelta > 0 ? '+' : ''}{dragState.daysDelta}d)
                                </span>
                              </div>
                            )}

                            {/* Blocked-by-mandated warning during drag */}
                            {isDragging && dragState.blockedByMandated && (
                              <div
                                className="absolute z-40 bg-red-600 text-white text-[10px] font-medium px-2 py-1 rounded shadow-lg pointer-events-none whitespace-nowrap"
                                style={{ left: barLeft, top: -22 }}
                              >
                                Blocked: would push past &quot;{dragState.blockedTaskTitle}&quot;
                              </div>
                            )}

                            {/* Statutory deadline diamond */}
                            {bar.hasDeadline && (
                              <div
                                className="absolute top-0.5"
                                style={{ left: bar.left + bar.width - 4 }}
                                title="Statutory deadline"
                              >
                                <svg width="8" height="8" viewBox="0 0 8 8" className="text-red-500 fill-current">
                                  <polygon points="4,0 8,4 4,8 0,4" />
                                </svg>
                              </div>
                            )}

                            {/* Hover tooltip (only when not dragging) */}
                            {hoveredTaskId === task.id && !dragState && (
                              <div
                                className="absolute z-30 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-2 text-xs pointer-events-none"
                                style={{
                                  left: Math.min(bar.left + bar.width / 2, chartWidth - 200),
                                  top: ROW_HEIGHT,
                                  width: 200,
                                }}
                              >
                                <div className="font-medium text-gray-900 dark:text-white truncate">{task.title}</div>
                                <div className="text-gray-500 dark:text-gray-400 mt-1">
                                  {STATUS_LABELS[task.status]} &middot; {task.priority}
                                </div>
                                {task.due_date && (
                                  <div className="text-gray-500 dark:text-gray-400">
                                    Due: {new Date(task.due_date).toLocaleDateString()}
                                  </div>
                                )}
                                {task.assignee && (
                                  <div className="text-gray-500 dark:text-gray-400">
                                    {task.assignee.full_name || task.assignee.email}
                                  </div>
                                )}
                                {isMandated && (
                                  <div className="text-red-600 dark:text-red-400 font-medium mt-1">Court Mandated (locked)</div>
                                )}
                                {!isMandated && (
                                  <div className="text-lex-accent mt-1">Drag to reschedule</div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>

      {/* ── Drag Confirm Dialog ── */}
      {showConfirmDialog && draggedTaskChange && (
        <GanttDragConfirmDialog
          taskTitle={draggedTaskChange.title}
          daysDelta={dragState?.daysDelta ?? 0}
          oldStartDate={draggedTaskChange.oldStartDate}
          oldDueDate={draggedTaskChange.oldDueDate}
          newStartDate={draggedTaskChange.newStartDate}
          newDueDate={draggedTaskChange.newDueDate}
          affectedTasks={pendingChanges}
          saving={saving}
          onConfirm={handleDragConfirm}
          onCancel={rejectDrag}
        />
      )}
    </div>
  );
};

export default GanttView;
